import * as React from "react";
import { Redirect } from "react-router-dom";

import { resendLoginOtp, verifyLoginOtp } from "api/auth";
import { getApiErrorCode, getApiErrorMessage } from "api/apiError";
import { Button, FormField, notify, OtpInput } from "Components/ui";
import { useAuth } from "hooks/useAuth";
import { formatCountdown, useCountdown } from "hooks/useCountdown";
import { StepHeader } from "./AuthLayout";
import { useAuthFlow } from "./data";
import { AUTH_PATHS, type AuthNotice } from "./types";

const CODE_LENGTH = 6;

// Codes that end this login attempt: the user has to enter the password again.
const RESTART_NOTICES: Record<string, AuthNotice> = {
  AUTH_OTP_EXPIRED: {
    tone: "warning",
    title: "Verification code expired",
    message: "Please sign in again to receive a new code.",
  },
  AUTH_OTP_EXHAUSTED: {
    tone: "warning",
    title: "Too many incorrect codes",
    message: "For your security, please sign in again to receive a new code.",
  },
};

const VerifyStep = () => {
  const flow = useAuthFlow();
  const { signIn } = useAuth();
  const challenge = flow.challenge;
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string | null>(null);
  const [verifying, setVerifying] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [cooldown, restartCooldown] = useCountdown(challenge?.resendAfterSeconds ?? 30);
  const inputRef = React.useRef<HTMLInputElement>(null);

  if (!challenge) {
    return <Redirect to={AUTH_PATHS.signIn} />;
  }

  const restartIfOver = (err: unknown): boolean => {
    const notice = RESTART_NOTICES[getApiErrorCode(err) || ""];
    if (notice) {
      flow.backToSignIn(notice);
      return true;
    }
    return false;
  };

  const verify = async (value: string) => {
    if (verifying) {
      return;
    }
    if (value.length !== CODE_LENGTH) {
      setError(`Enter the full ${CODE_LENGTH}-digit code.`);
      inputRef.current?.focus();
      return;
    }
    setError(null);
    setStatus(null);
    setVerifying(true);
    try {
      const location = flow.readLocation();
      const session = await verifyLoginOtp({
        userId: challenge.userId,
        code: value,
        ...(location ? { location } : {}),
      });
      signIn(session.data.token, session.user as unknown as Record<string, unknown>);
      notify.success(`Welcome back, ${session.user.name}`);
    } catch (err) {
      setVerifying(false);
      if (restartIfOver(err)) {
        return;
      }
      setError(getApiErrorMessage(err, "We couldn't verify your code. Please try again."));
      setCode("");
      inputRef.current?.focus();
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) {
      return;
    }
    setResending(true);
    setError(null);
    setStatus(null);
    try {
      const res = await resendLoginOtp(challenge.userId);
      flow.updateChallenge({
        phoneHint: res.phoneHint || challenge.phoneHint,
        expiresInSeconds: res.expiresInSeconds,
        resendAfterSeconds: res.resendAfterSeconds,
      });
      restartCooldown(res.resendAfterSeconds);
      setCode("");
      setStatus("A new code has been sent. Earlier codes no longer work.");
      inputRef.current?.focus();
    } catch (err) {
      if (!restartIfOver(err)) {
        setError(
          getApiErrorMessage(err, "We couldn't send a new code. Please try again in a moment.")
        );
      }
    } finally {
      setResending(false);
    }
  };

  const destination = challenge.phoneHint ? (
    <span className="whitespace-nowrap font-medium text-foreground">{challenge.phoneHint}</span>
  ) : (
    "the WhatsApp number on your account"
  );
  const validMinutes = Math.max(1, Math.round(challenge.expiresInSeconds / 60));

  return (
    <>
      <StepHeader
        back={{ label: "Use a different account", onClick: () => flow.backToSignIn() }}
        step={{ current: 2, total: 2, label: "Sign in" }}
        title="Check your WhatsApp"
        description={
          <>
            Enter the {CODE_LENGTH}-digit code we sent to {destination}. It's valid for{" "}
            {validMinutes} minutes.
          </>
        }
      />

      <form
        noValidate
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          verify(code);
        }}
      >
        <FormField label="Verification code" error={error}>
          <OtpInput
            ref={inputRef}
            value={code}
            onChange={(value) => {
              setCode(value);
              if (error) setError(null);
            }}
            onComplete={verify}
            length={CODE_LENGTH}
            disabled={verifying}
            autoFocus
          />
        </FormField>

        {status && (
          <p role="status" className="text-sm text-success-text">
            {status}
          </p>
        )}

        <Button type="submit" size="lg" fullWidth loading={verifying}>
          Verify and sign in
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Didn't get a code?{" "}
          {cooldown > 0 ? (
            <span className="tabular-nums">Resend in {formatCountdown(cooldown)}</span>
          ) : (
            <Button variant="link" onClick={resend} loading={resending} className="align-baseline">
              Resend code
            </Button>
          )}
        </p>
      </form>
    </>
  );
};

export default VerifyStep;
